
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ClientCommandsService } from '@services/ClientCommands.service';
import { RouterHistoryService } from './@core/services/RouterHistory.service';
import { HttpResponseInterceptor } from './@framework/http/http-response.interceptor';
import { AuthInterceptor } from './@core/Helpers/Interceptor';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './@core/core.module';
import { zAppMenuControlComponent } from '@framework/components/MenuControl/MenuControl.component';
import { zAppDevButtonModule } from './@framework/components/Button/button.module';
import { ZAppDevThemeModule } from './@framework/theme/theme.module';
import { IThemeSevice } from './@framework/theme/interfaces/theme.interface';
import {LightThemeService} from './@theming/services/LightTheme.services';
import { ThemeService } from './@theming/theme.service';
import { ListControlResourcesService } from '@framework/components/DataList/helpers/resources.service';
import { environment } from '../environments/environment';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { first } from 'rxjs/operators';
import { PagesService } from './pages/pages.services';
import { PermissionService } from './@framework/security/services/permission.service';
import { Joove } from './@framework/core/Joove';
import { IPagesService } from './pages/pages.interface';
import { RequestInterceptor } from '@core/Helpers/RequestInterceptor';
import { ContextService } from '@core/services/Context.service';
import { FormsModule } from '@angular/forms';
import { zAppDevFormModalModule } from '@framework/components/FormModal/form-modal.module';
import { GoogleMapsService } from './@core/services/GoogleMaps.service';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import * as Domain from '@core/Domain';

export function appInitializerFactory(
  pagesService: PagesService, 
  permissionService: PermissionService, 
  contextService: ContextService, 
  translateService: TranslateService, 
  googleMapsService: GoogleMapsService,
  themeService: ThemeService,
  injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    pagesService.refreshContext('MasterPage', 'Render').pipe(first()).subscribe((context: Joove.IContext) => {
      Domain.DomainIit.Init(translateService);
      permissionService.updatePermissions(context.currentUserPermissions);
      contextService.update(context);
      googleMapsService.loadGoogleMapsScript(context.appSettings.googleMapsAPIKey);
      themeService.setThemes(['default']);
      themeService.setMainTheme(context.theme);
      translateService.use(context.currentLanguage.languageIetTag).pipe(first()).subscribe(() => {
        resolve(true);
      });
    });

  });
}

const DATA_SERVICES = [
  { provide: IThemeSevice, useClass: LightThemeService },
  ContextService,
  GoogleMapsService,
  ClientCommandsService,
  RouterHistoryService,
  ListControlResourcesService,
  providePrimeNG({
    theme: {
            preset: Aura,
            options: {
                darkModeSelector: false || 'none'
            }
        }
  }),
  ConfirmationService,
  { provide: IPagesService, useClass: PagesService },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpResponseInterceptor, multi: true },
  provideAppInitializer(() => {
        const initializerFn = (appInitializerFactory)(inject(PagesService), inject(PermissionService), inject(ContextService), inject(TranslateService), inject(GoogleMapsService), inject(ThemeService), inject(Injector));
        return initializerFn();
      }),
];

export function createTranslateLoader(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: `${environment.i18nUrl}/assets/i18n/app/`, suffix: `.json?v=${environment.semanticVersion}` },
    { prefix: `${environment.i18nUrl}/assets/i18n/domain/`, suffix: `.json?v=${environment.semanticVersion}` },
    { prefix: `${environment.i18nUrl}/assets/i18n/primeng/`, suffix: '.json' },
  ]);
}

const reducers = {};

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  exports: [],
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    CoreModule.forRoot(),
    zAppMenuControlComponent,
    zAppDevButtonModule.forRoot(),
    AppRoutingModule,
    zAppDevFormModalModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en-us',
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpBackend]
      },
    }),
    ZAppDevThemeModule.forRoot(),
    ConfirmPopupModule,
    ConfirmDialogModule,
  ],
  providers: [...DATA_SERVICES, provideHttpClient(withInterceptorsFromDi())],
  
})

export class AppModule { }

// required for AOT compilation
export function HttpLoaderFactory (http: HttpClient) {
  return new TranslateHttpLoader(http);
}
